import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Download, Copy, Printer, Columns, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { vehicleService } from '@/services/vehicleService'
import { contractService } from '@/services/contractService'
import type { VehicleListItem } from '@/api/types/vehicle.types'
import type { EmployeeOption } from '@/api/types'
import { useEmployeeListScope } from '@/hooks/useEmployeeListScope'
import { EmployeeListScopeDialog } from '@/components/employee-scope/EmployeeListScopeDialog'

function formatDate(d?: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(d).slice(0, 10)
  }
}

export const VehicleList: React.FC = () => {
  const { t } = useTranslation('common')
  const tv = (key: string) => t(`empVehicle.${key}`)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const { empId, pickerOpen, confirmEmployee, openEmployeePicker, handlePickerOpenChange } = useEmployeeListScope()

  const [list, setList] = useState<VehicleListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [pendingAdd, setPendingAdd] = useState(false)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employeeName: true,
    vehicleCompanyName: true,
    vehicleColor: true,
    model: true,
    carNumber: true,
    createdDate: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  useEffect(() => {
    if (!branchId || !empId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await vehicleService.getAll(branchId, empId, pageNumber, pageSize)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setList([])
        setTotalRecords(0)
        if (!isNoData) toast.error(e?.response?.data?.message ?? tv('fetchFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, empId, pageNumber, pageSize])

  useEffect(() => {
    if (empId) return
    setList([])
    setTotalRecords(0)
    setSearchQuery('')
    setPageNumber(1)
    setPendingAdd(false)
  }, [empId])

  useEffect(() => {
    if (!pendingAdd || !empId) return
    setPendingAdd(false)
    navigate(`/vehicles/new?empId=${empId}`)
  }, [pendingAdd, empId, navigate])

  const getEmployeeName = (empId?: number): string => {
    if (!empId) return '—'
    const emp = employees.find((e) => e.empId === empId)
    if (!emp) return t('empVehicle.employeeFallback', { id: empId })
    return (
      (emp.fullName ?? [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()) ||
      t('empVehicle.employeeFallback', { id: empId })
    )
  }

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(
      (r) =>
        getEmployeeName(r.empId).toLowerCase().includes(q) ||
        (r.vehicleCompanyName ?? '').toLowerCase().includes(q) ||
        (r.vehicleColor ?? r.modelColor ?? '').toLowerCase().includes(q) ||
        String(r.model ?? r.modelYear ?? '').toLowerCase().includes(q) ||
        (r.carNumber ?? r.carNumberPlate ?? r.vehicleNumber ?? '').toLowerCase().includes(q)
    )
  }, [list, searchQuery, employees, t])

  const paginatedRows = filteredList
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize))
  const visibleColumnCount =
    [columnVisibility.employeeName, columnVisibility.vehicleCompanyName, columnVisibility.vehicleColor, columnVisibility.model, columnVisibility.carNumber, columnVisibility.createdDate, columnVisibility.action].filter(Boolean).length || 1

  const handleAdd = () => navigate(`/vehicles/new?empId=${empId}`)
  const handleAddClick = () => {
    if (empId) return handleAdd()
    setPendingAdd(true)
    openEmployeePicker()
  }
  const handleEdit = (id: number) => navigate(`/vehicles/${id}/edit?empId=${empId}`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(tv('deleteTitle'), tv('deleteConfirm'))
    if (!confirmed) return
    setDeletingId(id)
    try {
      await vehicleService.delete(id)
      await showSuccess(tv('deletedTitle'), tv('deletedMessage'))
      if (branchId && empId) {
        const res = await vehicleService.getAll(branchId, empId, pageNumber, pageSize)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError(tv('deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? tv('deleteFailedMessage'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = [
      tv('columnEmployeeName'),
      tv('columnVehicleCompanyName'),
      tv('columnVehicleColor'),
      tv('columnModel'),
      tv('columnCarNumber'),
      tv('columnCreatedDate'),
    ]
    const rows = filteredList.map((r) =>
      [
        getEmployeeName(r.empId),
        r.vehicleCompanyName ?? '',
        r.vehicleColor ?? r.modelColor ?? '',
        r.model ?? r.modelYear ?? '',
        r.carNumber ?? r.carNumberPlate ?? r.vehicleNumber ?? '',
        r.createdDate ? formatDate(r.createdDate) : '',
      ].join(',')
    )
    const csv = [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = tv('csvFileName')
    a.click()
    URL.revokeObjectURL(url)
    toast.success(tv('csvDownloaded'))
  }

  const handleCopy = () => {
    const text = filteredList
      .map((r) =>
        [
          getEmployeeName(r.empId),
          r.vehicleCompanyName ?? '',
          r.vehicleColor ?? r.modelColor ?? '',
          r.model ?? r.modelYear ?? '',
          r.carNumber ?? r.carNumberPlate ?? r.vehicleNumber ?? '',
          r.createdDate ? formatDate(r.createdDate) : '',
        ].join('\t')
      )
      .join('\n')
    navigator.clipboard.writeText(text).then(() => toast.success(tv('copiedToClipboard')))
  }

  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={tv('listTitle')} description={tv('listDescriptionShort')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{tv('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <EmployeeListScopeDialog
        open={pickerOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingAdd(false)
          handlePickerOpenChange(nextOpen)
        }}
        branchId={branchId}
        onConfirm={confirmEmployee}
        currentEmpId={empId}
      />
      <PageHeader
        title={tv('listTitle')}
        description={
          empId
            ? `${tv('listDescription')} · ${t('employeeListScope.selectedEmployee', { id: empId })}`
            : tv('listDescription')
        }
        action={{ label: t('common.add'), onClick: handleAddClick, icon: Plus, disabled: !empId }}
      >
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openEmployeePicker}>
          <Users className="h-4 w-4" />
          {t('employeeListScope.changeEmployee')}
        </Button>
      </PageHeader>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-2">
          <div className="relative w-64 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              disabled={!empId}
            />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!empId}>
                  <Columns className="w-4 h-4 mr-2" />
                  {tv('columnVisibility')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px]">
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.employeeName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, employeeName: !!v }))}
                >
                  {tv('columnEmployeeName')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.vehicleCompanyName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, vehicleCompanyName: !!v }))}
                >
                  {tv('columnVehicleCompanyName')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.vehicleColor}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, vehicleColor: !!v }))}
                >
                  {tv('columnVehicleColor')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.model}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, model: !!v }))}
                >
                  {tv('columnModel')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.carNumber}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, carNumber: !!v }))}
                >
                  {tv('columnCarNumber')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.createdDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}
                >
                  {tv('columnCreatedDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.action}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, action: !!v }))}
                >
                  {tv('columnAction')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv} disabled={!empId}>
              <Download className="w-4 h-4 mr-2" />
              {tv('csv')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={!empId}>
              <Copy className="w-4 h-4 mr-2" />
              {tv('copy')}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={!empId}>
              <Printer className="w-4 h-4 mr-2" />
              {tv('print')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {columnVisibility.employeeName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnEmployeeName')}
                    </TableHead>
                  )}
                  {columnVisibility.vehicleCompanyName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnVehicleCompanyName')}
                    </TableHead>
                  )}
                  {columnVisibility.vehicleColor && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnVehicleColor')}
                    </TableHead>
                  )}
                  {columnVisibility.model && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnModel')}
                    </TableHead>
                  )}
                  {columnVisibility.carNumber && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnCarNumber')}
                    </TableHead>
                  )}
                  {columnVisibility.createdDate && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnCreatedDate')}
                    </TableHead>
                  )}
                  {columnVisibility.action && (
                    <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tv('columnAction')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!empId ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-10 text-muted-foreground">
                      {t('employeeListScope.dialogDescription', {
                        defaultValue:
                          'Choose an employee to load their records. You can change this later from the list page.',
                      })}
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="p-0">
                      <ListLoader message={tv('loading')} />
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {tv('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                    >
                      {columnVisibility.employeeName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {getEmployeeName(row.empId)}
                        </TableCell>
                      )}
                      {columnVisibility.vehicleCompanyName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.vehicleCompanyName ?? '—'}</TableCell>
                      )}
                      {columnVisibility.vehicleColor && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{(row.vehicleColor ?? row.modelColor) ?? '—'}</TableCell>
                      )}
                      {columnVisibility.model && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.model ?? row.modelYear ?? '—'}</TableCell>
                      )}
                      {columnVisibility.carNumber && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {(row.carNumber ?? row.carNumberPlate ?? row.vehicleNumber) ?? '—'}
                        </TableCell>
                      )}
                      {columnVisibility.createdDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.createdDate)}</TableCell>
                      )}
                      {columnVisibility.action && (
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(row.id!)}
                              className="h-8 w-8 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(row.id!)}
                              disabled={deletingId === row.id}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              {t('empVehicle.paginationShowing', {
                from: totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                to: Math.min(pageNumber * pageSize, totalRecords),
                total: totalRecords,
              })}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
                disabled={!empId}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={!empId || pageNumber <= 1 || loading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {t('empVehicle.pageOf', { page: pageNumber, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!empId || pageNumber >= totalPages || loading || totalRecords === 0}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
